/**
 * Saneamento e contagem do texto rico (P-FUN-2b).
 *
 * O objeto, as condicoes, as clausulas e os e-mails de aviso sao escritos num editor
 * que produz HTML, como o "Editor otimizado para Word" do OnPoint. HTML vindo de quem
 * digita ou cola e entrada nao confiavel: um atributo de evento ou um script colado
 * de outra pagina rodaria na sessao de quem abrir o texto depois.
 *
 * O saneamento e por lista de permissao e por reconstrucao: o texto e lido num
 * documento inerte (DOMParser nao executa script nem carrega imagem) e reescrito
 * elemento por elemento, com um conjunto fechado de marcas e de estilos. O que nao
 * esta na lista vira texto corrido (a marca sai, o conteudo fica); o que e perigoso
 * sai com o conteudo. Nenhum atributo atravessa, salvo o estilo filtrado e o inicio
 * da lista numerada.
 *
 * As marcas cobrem a barra do OnPoint (negrito, italico, sublinhado, tachado,
 * sobrescrito, subscrito, fonte, tamanho, paragrafo e titulo) e as listas. O servidor
 * le o mesmo texto com um leitor ainda mais estreito (P-MIN-1, textorico): o que sai
 * daqui o documento sabe imprimir.
 *
 * Sem biblioteca: o navegador ja traz o leitor de HTML, e a lista curta cabe aqui com
 * teste. Uma biblioteca de saneamento generica traria regras para marcas que o editor
 * nunca produz.
 */

/** Marcas mantidas, com a troca das equivalentes (b vira strong, i vira em). */
const marcas: Record<string, string> = {
  p: 'p',
  br: 'br',
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  u: 'u',
  ins: 'u',
  s: 's',
  strike: 's',
  del: 's',
  sup: 'sup',
  sub: 'sub',
  ul: 'ul',
  ol: 'ol',
  li: 'li',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  blockquote: 'blockquote',
  span: 'span',
};

/** Marcas que saem com todo o conteudo: codigo, midia, formulario e metadado. */
const descartadas = new Set([
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'svg',
  'math',
  'template',
  'noscript',
  'link',
  'meta',
  'base',
  'head',
  'title',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'img',
  'picture',
  'video',
  'audio',
  'source',
  'track',
  'canvas',
  'xml',
]);

/** Blocos: a marca que nao fica, quando e bloco, vira paragrafo, e nao texto colado. */
const blocos = new Set([
  'p',
  'div',
  'section',
  'article',
  'header',
  'footer',
  'aside',
  'main',
  'nav',
  'address',
  'pre',
  'tr',
  'h5',
  'h6',
  'dt',
  'dd',
  'figure',
  'figcaption',
]);

const blocosMantidos = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'li', 'blockquote']);

/** Fontes da barra do OnPoint. A fonte colada fora delas cai. */
export const FONTES_DO_EDITOR = ['Arial', 'Calibri'] as const;

const alinhamentos = new Set(['left', 'right', 'center', 'justify']);

/**
 * Estilos permitidos, com o valor conferido. Nada de url(), expression() nem
 * propriedade fora da lista: o filtro e por valor, e nao por exclusao.
 */
function estiloLimpo(marca: string, estilo: CSSStyleDeclaration): string {
  const partes: string[] = [];
  const alinhamento = estilo.getPropertyValue('text-align').trim().toLowerCase();
  if (blocosMantidos.has(marca) && alinhamentos.has(alinhamento)) {
    partes.push(`text-align: ${alinhamento}`);
  }
  if (marca === 'span') {
    const familia = estilo
      .getPropertyValue('font-family')
      .split(',')
      .map((nome) => nome.trim().replace(/^["']|["']$/g, ''))
      .find((nome) =>
        FONTES_DO_EDITOR.some((f) => f.toLowerCase() === nome.toLowerCase()),
      );
    if (familia) {
      const canonica = FONTES_DO_EDITOR.find(
        (f) => f.toLowerCase() === familia.toLowerCase(),
      );
      partes.push(`font-family: ${canonica}`);
    }
    const tamanho = /^(\d{1,2}(?:\.\d)?)(px|pt)$/.exec(
      estilo.getPropertyValue('font-size').trim().toLowerCase(),
    );
    if (tamanho) {
      const valor = Number(tamanho[1]);
      if (valor >= 6 && valor <= 72)
        partes.push(`font-size: ${tamanho[1]}${tamanho[2]}`);
    }
  }
  return partes.join('; ');
}

/** Um bloco que so tem conteudo em linha vira paragrafo; o que tem blocos se desfaz. */
function temBlocoDentro(elemento: Element): boolean {
  return Array.from(elemento.children).some((filho) => {
    const nome = filho.tagName.toLowerCase();
    return (
      blocos.has(nome) || blocosMantidos.has(nome) || nome === 'ul' || nome === 'ol'
    );
  });
}

function copiar(origem: Node, destino: Node, documento: Document): void {
  for (const no of Array.from(origem.childNodes)) {
    if (no.nodeType === Node.TEXT_NODE) {
      destino.appendChild(documento.createTextNode(no.textContent ?? ''));
      continue;
    }
    if (no.nodeType !== Node.ELEMENT_NODE) continue; // comentario, instrucao, CDATA

    const elemento = no as Element;
    const nome = elemento.tagName.toLowerCase();
    if (descartadas.has(nome) || nome.includes(':')) {
      // Marca de outro vocabulario (o:p do Word, svg dentro de html) sai inteira.
      continue;
    }

    const alvo = marcas[nome];
    if (!alvo) {
      if (blocos.has(nome) && !temBlocoDentro(elemento)) {
        const paragrafo = documento.createElement('p');
        const estilo = estiloLimpo('p', (elemento as HTMLElement).style);
        if (estilo) paragrafo.setAttribute('style', estilo);
        copiar(elemento, paragrafo, documento);
        destino.appendChild(paragrafo);
      } else {
        // Desfaz a marca e fica com o conteudo. Celula de tabela ganha um espaco,
        // para que as colunas nao grudem.
        copiar(elemento, destino, documento);
        if (nome === 'td' || nome === 'th')
          destino.appendChild(documento.createTextNode(' '));
      }
      continue;
    }

    if (alvo === 'p' && temBlocoDentro(elemento)) {
      // Paragrafo com bloco dentro nao existe no HTML; o conteudo sobe um nivel.
      copiar(elemento, destino, documento);
      continue;
    }

    const novo = documento.createElement(alvo);
    const estilo = estiloLimpo(alvo, (elemento as HTMLElement).style);
    if (estilo) novo.setAttribute('style', estilo);
    if (alvo === 'ol') {
      const inicio = Number(elemento.getAttribute('start'));
      if (Number.isInteger(inicio) && inicio > 1 && inicio < 10_000) {
        novo.setAttribute('start', String(inicio));
      }
    }
    if (alvo !== 'br') copiar(elemento, novo, documento);

    // span sem estilo nao diz nada: fica so o conteudo.
    if (alvo === 'span' && !estilo) {
      while (novo.firstChild) destino.appendChild(novo.firstChild);
      continue;
    }
    destino.appendChild(novo);
  }
}

/** Texto rico vazio: so marcas, espacos e quebras, sem nenhum caractere visivel. */
function semConteudo(raiz: Element): boolean {
  return (raiz.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '') === '';
}

/**
 * Devolve o HTML saneado. Texto vazio (inclusive o paragrafo vazio que o editor deixa)
 * volta como texto vazio, para que "nao preenchido" seja um valor so.
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  const lido = new DOMParser().parseFromString(html, 'text/html');
  const documento = document.implementation.createHTMLDocument('');
  const raiz = documento.createElement('div');
  copiar(lido.body, raiz, documento);
  if (semConteudo(raiz)) return '';
  return raiz.innerHTML;
}

/** Texto puro do HTML, com uma quebra de linha entre blocos e itens de lista. */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return '';
  const lido = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
  const partes: string[] = [];
  let linha = '';
  const fechar = () => {
    if (linha.trim() !== '') partes.push(linha.trim());
    linha = '';
  };
  const andar = (no: Node) => {
    for (const filho of Array.from(no.childNodes)) {
      if (filho.nodeType === Node.TEXT_NODE) {
        linha += filho.textContent ?? '';
        continue;
      }
      if (filho.nodeType !== Node.ELEMENT_NODE) continue;
      const nome = (filho as Element).tagName.toLowerCase();
      if (nome === 'br') {
        fechar();
        continue;
      }
      const bloco = blocosMantidos.has(nome) || nome === 'ul' || nome === 'ol';
      if (bloco) fechar();
      andar(filho);
      if (bloco) fechar();
    }
  };
  andar(lido.body);
  fechar();
  return partes.join('\n').replace(/\u00a0/g, ' ');
}

/**
 * Contagem do rodape do editor, como a do OnPoint: caracteres do texto (com os
 * espacos, sem as quebras de linha) e palavras.
 */
export function countText(html: string | null | undefined): {
  characters: number;
  words: number;
} {
  const texto = htmlToText(html);
  const characters = Array.from(texto.replace(/\n/g, '')).length;
  const words = texto.split(/\s+/).filter((palavra) => palavra !== '').length;
  return { characters, words };
}
